import React, { useEffect, useMemo, useState, useLayoutEffect } from "react";
import Sidebar from "./sidebar/Sidebar";
import styles from "./Main.module.scss";
import AppPageWrapper from "./content/orderAd/AppPageWrapper";
import Profile from "./content/profile/Profile";
import { AccountType } from "../core/auth/auth.entity";
import { useRequest } from "../hooks/useRequest";
import { getPanelsThunk } from "../core/auth/auth.thunk";
import { getConfigThunk } from "../core/configs/configs.thunk";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../utils/store";
import {
  AppPage,
  AppPageUrls,
  NavigationActions,
  SidebarOption,
} from "../core/navigation/navigation.slice";
import { inquireNPSThunk } from "core/nps/nps.thunk";
import { useConfigs } from "hooks/useConfigs";
import FullpageModal from "./commons/modal/fullpageModal/FullpageModal";
import LoadingSpinner from "./commons/loading/LoadingSpinner";
import {
  GetDefaultCampaignPackagesThunk,
  getUserCustomCampaignPackageThunk,
  submitAdOrderThunk,
} from "core/orderAd/orderAd.thunk";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { GetPanelsErrors } from "core/auth/auth.error";
import { GetWheelStateThunk } from "core/luckyWheel/luckyWheel.thunk";

const Main: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { sendRequest: getPanel } = useRequest(getPanelsThunk);
  const { sendRequest: getConfig } = useRequest(getConfigThunk);
  const { sendRequest: inquireNPS } = useRequest(inquireNPSThunk);
  const { sendRequest: getCustomPacks } = useRequest(
    getUserCustomCampaignPackageThunk
  );
  const { sendRequest: getDefaultCampaignPackages } = useRequest(
    GetDefaultCampaignPackagesThunk
  );
  useRequest(GetWheelStateThunk, true);

  const { owner_id, name } = useAppSelector((state) => state.auth.myPanel);

  const { cool_down_milli_second } = useConfigs();
  const navigate = useNavigate();
  const onStartApp = () => {
    inquireNPS();
    getConfig();
    getDefaultCampaignPackages();
    setTimeout(() => {
      getPanel()
        .catch((error) => {
          if (error === GetPanelsErrors.NoRegisteredPanel) {
            navigate("/hello", { replace: true });
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, cool_down_milli_second);
    setTimeout(() => {
      getCustomPacks();
    }, cool_down_milli_second * 2);
  };

  useEffect(() => {
    Sentry.setUser({ id: owner_id, username: name });
  }, [owner_id, name]);

  useEffect(() => {
    onStartApp();
  }, []);

  const search = useLocation();
  const dispatch = useAppDispatch();

  // TODO - refactor this to iterate on AppPageUrls and setAppPage to key of pathname in AppPageUrls object if exist.
  useLayoutEffect(() => {
    switch (search.pathname) {
      case "/tutorial":
        dispatch(NavigationActions.setAppPage(AppPage.Tutorial));
        break;
      case "/tutorialArticles":
        dispatch(NavigationActions.setAppPage(AppPage.TutorialArticles));
        break;
      case "/tutorialVideos":
        dispatch(NavigationActions.setAppPage(AppPage.TutorialVideos));
        break;

      case AppPageUrls[AppPage.AdvertisementPrices]:
        dispatch(NavigationActions.setAppPage(AppPage.AdvertisementPrices));
        break;

      default:
        dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
    }
  }, []);

  const currentSidebarOption = useAppSelector(
    (state) => state.navigation.sidebar
  );

  const CurrentPage = useMemo(() => {
    switch (currentSidebarOption) {
      case SidebarOption.OrderAdvertisement:
        return <AppPageWrapper />;
      case SidebarOption.Profile:
        return <Profile userType={AccountType.Organization} />;
      default:
        return <AppPageWrapper />;
    }
  }, [currentSidebarOption]);

  return (
    <div className={styles.Container}>
      <Sidebar />
      <div className={styles.Content}>{CurrentPage}</div>
      <div id="fullscreen-modal"></div>
      {isLoading && (
        <FullpageModal>
          <LoadingSpinner />
        </FullpageModal>
      )}
    </div>
  );
};

export default Main;
